import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Download, FileText, Printer } from "lucide-react";
import { getCustomers, getCustomerStatement, getSocietyStatement, getSettings } from "@/lib/api";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/09a62dc9-6a9e-4bad-b528-41b496ab2382/images/cbcacb73a9857c56eaa830e6342d9e2d9c033cf8fd7db1c24131862abae98730.png";

export default function Statement() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({});
  const [mode, setMode] = useState(searchParams.get("mode") || "customer");
  const [customerId, setCustomerId] = useState(searchParams.get("cid") || "");
  const [society, setSociety] = useState(searchParams.get("society") || "");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split("T")[0];
  });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split("T")[0]);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCustomers().then(setCustomers).catch(() => {});
    getSettings().then(setSettings).catch(() => {});
  }, []);

  const societies = useMemo(() => Array.from(new Set(customers.map((c) => c.society).filter(Boolean))).sort(), [customers]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      if (mode === "customer" && customerId) {
        setStatement(await getCustomerStatement(customerId, dateFrom, dateTo));
      } else if (mode === "society" && society) {
        setStatement(await getSocietyStatement(society, dateFrom, dateTo));
      }
    } catch (e) {
      setStatement(null);
    }
    setLoading(false);
  };

  const generatePDF = () => {
    if (!statement) return;
    const doc = new jsPDF();
    const cName = settings.companyName || "SUPERFLAME GAS SERVICES";
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(cName, 14, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(settings.address || "", 14, 24);
    doc.text(`Phone: ${settings.phone || ""}`, 14, 28);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(mode === "customer" ? "Customer Statement" : `Society Statement: ${statement.society}`, 14, 38);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    let y = 44;
    if (mode === "customer" && statement.customer) {
      const c = statement.customer;
      doc.text(`Name: ${c.name}  |  Consumer No: ${c.consumerNumber}  |  Society: ${c.society}`, 14, y); y += 5;
      doc.text(`Flat/Plot: ${c.flatNo || c.plotNo || "—"}  |  Phone: ${c.phone || "—"}`, 14, y); y += 5;
    }
    if (mode === "society") {
      doc.text(`Customers: ${statement.customerCount}`, 14, y); y += 5;
    }
    doc.text(`Period: ${dateFrom} to ${dateTo}`, 14, y); y += 5;
    doc.text(`Opening Balance: ${statement.openingBalance >= 0 ? "₹" + statement.openingBalance.toFixed(2) + " (Due)" : "Adv ₹" + Math.abs(statement.openingBalance).toFixed(2)}`, 14, y); y += 8;

    autoTable(doc, {
      startY: y,
      head: [["Date", "Description", "Debit (Bills)", "Credit (Payments)", "Balance"]],
      body: statement.entries.map((e) => [
        e.date, e.description,
        e.debit ? `₹${e.debit.toFixed(2)}` : "",
        e.credit ? `₹${e.credit.toFixed(2)}` : "",
        e.balance >= 0 ? `₹${e.balance.toFixed(2)}` : `Adv ₹${Math.abs(e.balance).toFixed(2)}`,
      ]),
      styles: { fontSize: 7, cellPadding: 2, font: "courier" },
      headStyles: { fillColor: [234, 88, 12], textColor: 255, fontSize: 7 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    const closingText = statement.closingBalance >= 0
      ? `Closing Balance: ₹${statement.closingBalance.toFixed(2)} (Outstanding)`
      : `Closing Balance: Advance ₹${Math.abs(statement.closingBalance).toFixed(2)}`;
    doc.text(closingText, 14, finalY);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on ${new Date().toLocaleString("en-IN")}`, 14, finalY + 6);
    doc.text(`FOR ${cName}`, 150, finalY + 6);

    const fileName = mode === "customer"
      ? `statement_${statement.customer?.consumerNumber || "customer"}_${dateFrom}_${dateTo}.pdf`
      : `statement_${statement.society || "society"}_${dateFrom}_${dateTo}.pdf`;
    doc.save(fileName);
  };

  const fmtBal = (v) => v >= 0 ? `₹${v.toFixed(2)}` : `Adv ₹${Math.abs(v).toFixed(2)}`;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="statement-page">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="outline" onClick={() => navigate(-1)} className="rounded-sm border-zinc-300"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">
          <FileText className="w-8 h-8 inline mr-2 text-orange-600" />Statement
        </h1>
      </div>

      {/* Controls */}
      <Card className="border border-zinc-200 rounded-sm shadow-none mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[120px]">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Type</Label>
              <Select value={mode} onValueChange={(v) => { setMode(v); setStatement(null); }}>
                <SelectTrigger data-testid="statement-mode" className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="society">Society</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === "customer" && (
              <div className="min-w-[200px]">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Customer</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger data-testid="statement-customer" className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8 mt-1"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.consumerNumber}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {mode === "society" && (
              <div className="min-w-[200px]">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Society</Label>
                <Select value={society} onValueChange={setSociety}>
                  <SelectTrigger data-testid="statement-society" className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8 mt-1"><SelectValue placeholder="Select society..." /></SelectTrigger>
                  <SelectContent>{societies.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">From</Label><Input data-testid="statement-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8 mt-1 w-36" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">To</Label><Input data-testid="statement-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8 mt-1 w-36" /></div>
            <Button data-testid="generate-statement" onClick={fetchStatement} disabled={loading} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm text-xs h-8">
              {loading ? "Loading..." : "Generate"}
            </Button>
            {statement && (
              <Button data-testid="download-statement-pdf" onClick={generatePDF} variant="outline" className="rounded-sm text-xs h-8">
                <Download className="w-3 h-3 mr-1" /> PDF
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Statement Preview */}
      {statement && (
        <Card className="border border-zinc-200 rounded-sm shadow-none">
          <CardHeader className="border-b border-zinc-200">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-heading font-black text-lg">{settings.companyName || "SUPERFLAME GAS SERVICES"}</p>
                <p className="text-[10px] text-zinc-500">{settings.address}</p>
              </div>
              <div className="text-right">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-orange-600">
                  {mode === "customer" ? "Customer Statement" : "Society Statement"}
                </CardTitle>
                <p className="text-xs text-zinc-500 mt-1">{dateFrom} to {dateTo}</p>
              </div>
            </div>
            {mode === "customer" && statement.customer && (
              <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 text-xs grid grid-cols-2 sm:grid-cols-4 gap-2">
                <p><span className="text-zinc-500">Name:</span> <b>{statement.customer.name}</b></p>
                <p><span className="text-zinc-500">Consumer No:</span> <b className="font-mono">{statement.customer.consumerNumber}</b></p>
                <p><span className="text-zinc-500">Society:</span> {statement.customer.society}</p>
                <p><span className="text-zinc-500">Phone:</span> {statement.customer.phone || "—"}</p>
              </div>
            )}
            {mode === "society" && (
              <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 text-xs">
                <p><span className="text-zinc-500">Society:</span> <b>{statement.society}</b> &middot; {statement.customerCount} customers</p>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {/* Opening Balance */}
            <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex justify-between text-sm font-mono">
              <span className="font-bold text-xs uppercase tracking-wider text-zinc-500">Opening Balance</span>
              <span className={`font-bold ${statement.openingBalance > 0 ? "text-red-600" : statement.openingBalance < 0 ? "text-emerald-600" : ""}`} data-testid="opening-balance">
                {fmtBal(statement.openingBalance)}
              </span>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-xs font-bold uppercase text-zinc-500">Date</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500">Description</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Debit</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Credit</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statement.entries.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-zinc-400 py-8">No transactions in this period.</TableCell></TableRow>
                ) : statement.entries.map((e, i) => (
                  <TableRow key={i} className={e.credit > 0 ? "bg-emerald-50/30" : ""}>
                    <TableCell className="text-xs font-mono">{e.date ? new Date(e.date).toLocaleDateString("en-IN") : "—"}</TableCell>
                    <TableCell className="text-xs">{e.description}</TableCell>
                    <TableCell className="text-xs font-mono text-right text-red-600">{e.debit ? `₹${e.debit.toFixed(2)}` : ""}</TableCell>
                    <TableCell className="text-xs font-mono text-right text-emerald-600">{e.credit ? `₹${e.credit.toFixed(2)}` : ""}</TableCell>
                    <TableCell className={`text-xs font-mono text-right font-semibold ${e.balance > 0 ? "text-red-600" : e.balance < 0 ? "text-emerald-600" : ""}`}>
                      {fmtBal(e.balance)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Closing Balance */}
            <div className="px-4 py-3 bg-zinc-100 border-t border-zinc-300 flex justify-between font-mono">
              <span className="font-bold text-xs uppercase tracking-wider text-zinc-700">Closing Balance</span>
              <span className={`font-bold text-base ${statement.closingBalance > 0 ? "text-red-600" : statement.closingBalance < 0 ? "text-emerald-600" : ""}`} data-testid="closing-balance">
                {statement.closingBalance > 0 ? `₹${statement.closingBalance.toFixed(2)} Outstanding` : statement.closingBalance < 0 ? `Advance ₹${Math.abs(statement.closingBalance).toFixed(2)}` : "Settled"}
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
